/**
 * Section Index — splits scaffold markdown files into addressable sections
 * with pre-computed token costs and keyword sets for the context router.
 *
 * Deterministic: no AI calls, no network. Operates on cached scaffold content.
 */
import { relative, join } from "node:path";
import { estimateTokens, stripFrontmatter } from "../utils/fs.js";
import { parseFrontmatter } from "../drift/frontmatter.js";
import type { FrontmatterEdge } from "../types.js";

export interface Section {
  /** Unique ID: "context/architecture.md#Key Components" */
  id: string;
  /** Relative file path from project root */
  file: string;
  /** Section heading text */
  heading: string;
  /** Heading level (1-6) */
  level: number;
  /** Full section content (heading line + body until next same/higher-level heading) */
  content: string;
  /** Pre-computed token cost */
  tokens: number;
  /** Lowercase keyword terms for matching */
  keywords: string[];
}

export interface FileEdges {
  file: string;
  targets: string[];
}

const STOPWORDS = new Set([
  "the", "a", "an", "is", "are", "was", "were", "be", "been", "being",
  "have", "has", "had", "do", "does", "did", "will", "would", "could",
  "should", "may", "might", "shall", "can", "need", "must",
  "and", "or", "but", "if", "then", "else", "when", "where", "how",
  "what", "which", "who", "whom", "this", "that", "these", "those",
  "for", "of", "in", "on", "at", "to", "from", "by", "with", "about",
  "into", "through", "during", "before", "after", "above", "below",
  "not", "no", "nor", "so", "very", "just", "also", "than", "too",
  "all", "each", "every", "any", "some", "it", "its", "we", "our",
]);

/**
 * Extract headings + the first non-empty line of each section — the exact
 * payload `cai_get_context` serves in its default "summary" mode (~10% of full).
 * Shared with the MCP server so the savings projection matches what's served.
 */
export function extractSummary(content: string): string {
  const lines = content.split("\n");
  const out: string[] = [];
  let inSection = false;
  let firstLineAdded = false;

  for (const line of lines) {
    if (/^#{1,6}\s/.test(line)) {
      out.push(line);
      inSection = true;
      firstLineAdded = false;
    } else if (inSection && !firstLineAdded && line.trim().length > 0) {
      out.push(line);
      firstLineAdded = true;
    }
  }
  return out.join("\n");
}

/** Extract keywords from text: lowercase, deduplicated, stopwords removed */
export function extractKeywords(text: string): string[] {
  const terms = text
    .toLowerCase()
    .replace(/[^a-z0-9\-_./]/g, " ")
    .split(/\s+/)
    .filter((t) => t.length > 1 && !STOPWORDS.has(t));
  return [...new Set(terms)];
}

/**
 * Split markdown content into sections at heading boundaries.
 * Each section includes its heading line and all content until the next
 * heading of equal or higher level.
 */
export function splitIntoSections(content: string, file: string): Section[] {
  const lines = content.split("\n");
  const sections: Section[] = [];
  let currentHeading = "";
  let currentLevel = 0;
  let currentLines: string[] = [];
  let hasHeading = false;

  function flush() {
    if (!hasHeading) {
      // Preamble before first heading — use file name as heading
      if (currentLines.some((l) => l.trim().length > 0)) {
        const text = currentLines.join("\n").trim();
        if (text.length > 0) {
          const heading = file.replace(/\.md$/, "").split("/").pop() ?? file;
          const keywordSource = heading + " " + currentLines.slice(0, 3).join(" ");
          sections.push({
            id: `${file}#(preamble)`,
            file,
            heading,
            level: 0,
            content: text,
            tokens: estimateTokens(text),
            keywords: extractKeywords(keywordSource),
          });
        }
      }
    } else if (currentHeading) {
      const text = currentLines.join("\n").trim();
      if (text.length > 0) {
        const keywordSource = currentHeading + " " + currentLines.slice(0, 3).join(" ");
        sections.push({
          id: `${file}#${currentHeading}`,
          file,
          heading: currentHeading,
          level: currentLevel,
          content: text,
          tokens: estimateTokens(text),
          keywords: extractKeywords(keywordSource),
        });
      }
    }
  }

  for (const line of lines) {
    const match = line.match(/^(#{1,6})\s+(.+)/);
    if (match) {
      flush();
      currentHeading = match[2].trim();
      currentLevel = match[1].length;
      currentLines = [line];
      hasHeading = true;
    } else {
      currentLines.push(line);
    }
  }
  flush();

  return sections;
}

/**
 * Build a section index from all scaffold files.
 * Takes a file-reader function to decouple from ScaffoldCache (testable).
 */
export function buildSectionIndex(
  scaffoldFiles: string[],
  projectRoot: string,
  readFile: (absPath: string) => string
): Section[] {
  const allSections: Section[] = [];

  for (const absPath of scaffoldFiles) {
    const rel = relative(projectRoot, absPath);
    const raw = readFile(absPath);
    const stripped = stripFrontmatter(raw);
    const sections = splitIntoSections(stripped, rel);
    allSections.push(...sections);
  }

  return allSections;
}

/**
 * Extract edge targets from scaffold files' frontmatter.
 * Returns a map of file → target files for the router's edge bonus.
 */
export function buildEdgeMap(
  scaffoldFiles: string[],
  projectRoot: string,
  scaffoldRoot: string
): Map<string, string[]> {
  const edgeMap = new Map<string, string[]>();

  for (const absPath of scaffoldFiles) {
    const rel = relative(projectRoot, absPath);
    const fm = parseFrontmatter(absPath);
    if (!fm?.edges || !Array.isArray(fm.edges)) continue;

    const targets: string[] = [];
    for (const edge of fm.edges as FrontmatterEdge[]) {
      if (edge.target) {
        // Edge targets are relative to scaffold root — normalize to project-relative
        const targetRel = relative(projectRoot, join(scaffoldRoot, edge.target));
        targets.push(targetRel);
      }
    }
    if (targets.length > 0) {
      edgeMap.set(rel, targets);
    }
  }

  return edgeMap;
}
